import { useCallback, useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
import {
  ensureLoaded,
  getServerSnapshot,
  getSnapshot,
  startAuthWatch,
  subscribe,
  toggleSaved,
  type ToggleResult,
} from "@/lib/wishlist-store";

/**
 * Saved products. Guests persist on this device; signed-in customers persist
 * in their own database rows (RLS scoped to auth.uid()). Every heart button
 * and the account list read the same shared state, so they never disagree.
 *
 * Only the product handle is ever stored or tracked — no contact details, no
 * quiz/health answers.
 */
export function useWishlist() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    ensureLoaded();
    return startAuthWatch();
  }, []);

  // Surface load/merge problems once, without wiping what we can still show.
  useEffect(() => {
    if (state.error) toast.error(state.error);
  }, [state.error]);

  const isSaved = useCallback(
    (handle: string) => state.handles.includes(handle.trim().toLowerCase()),
    [state.handles],
  );

  const isBusy = useCallback(
    (handle: string) => !state.ready || state.pending.includes(handle.trim().toLowerCase()),
    [state.ready, state.pending],
  );

  const toggle = useCallback(async (handle: string): Promise<ToggleResult> => {
    const result = await toggleSaved(handle);
    if (!result.ok && result.reason === "write_failed") {
      toast.error("Couldn't update your saved products. Please try again.");
    }
    return result;
  }, []);

  return {
    handles: state.handles,
    ready: state.ready,
    signedIn: state.signedIn,
    isSaved,
    isBusy,
    toggle,
  };
}
